import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { useCustomAuth } from "@/contexts/CustomAuthContext";
import { authApi } from '@/services/authService';
import { Header } from '@/components/layout/header/Header';
import {
  Search, Shield, User, Power, CreditCard, MousePointerClick,
  Send, X, Info, Percent
} from "lucide-react";
import { toast } from "sonner";

interface SubscriptionPlan {
  id?: string | null;
  object?: string | null;
  active?: boolean | null;
  aggregate_usage?: string | null;
  amount?: number | null;
  amount_decimal?: string | null;
  billing_scheme?: string | null;
  created?: number | null;
  currency?: string | null;
  interval?: string | null;
  interval_count?: number | null;
  livemode?: boolean | null;
  metadata?: Record<string, any> | null;
  meter?: string | null;
  nickname?: string | null;
  product?: string | null;
  tiers_mode?: string | null;
  transform_usage?: string | null;
  trial_period_days?: number | null;
  usage_type?: string | null;
}

interface Subscription {
  plan?: SubscriptionPlan | null;
  status?: string | null;
  id_subscription?: string | null;
  start_date?: number | null;
  trial_start?: number | null;
  trial_end?: number | null;
  created?: number | null;
  currency?: string | null;
  current_period_end?: number | null;
  current_period_start?: number | null;
  customer?: string | null;
}

interface UserDetails {
  id_user: string;
  nome: string;
  sobrenome: string;
  email: string;
  telefone?: string;
  cpf?: string;
  nivel: 'customer' | 'seller' | 'manager' | 'admin' | 'platform' | 'support';
  status: 'A' | 'D' | string;
  purchased_plan?: string;
  id_stripe_plan?: string;
  id_Stripe_Client?: string;
  clicks_per_plan?: number;
  plans?: [SubscriptionPlan];
  subscriptions?: { plan: SubscriptionPlan };
  seller_commission_percentage?: number | null;
}

export default function AdminUserTools() {
  useLanguage();
  const { user } = useCustomAuth();
  const navigate = useNavigate();

  // Proteção de Rota - Apenas Admin
  useEffect(() => {
    if (user && user.nivel !== 'admin') {
      toast.error(translateInterfaceText("Área restrita a administradores."));
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Estados de Controle
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [modalLoading, setModalLoading] = useState(false);

  // Lista de Planos para o Select
  const [plansList, setPlanList] = useState<SubscriptionPlan[]>([]);
  const [userSubscription, setUserSubscription] = useState<Subscription>({});

  // Estados dos Formulários
  const [newNivel, setNewNivel] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [newPlano, setNewPlano] = useState(userSubscription?.plan?.product);
  const [newClicks, setNewClicks] = useState<number>(0);
  const [newSellerCommission, setNewSellerCommission] = useState<string>('');

  const [submittingNivel, setSubmittingNivel] = useState(false);
  const [submittingStatus, setSubmittingStatus] = useState(false);
  const [submittingPlano, setSubmittingPlano] = useState(false);
  const [submittingClicks, setSubmittingClicks] = useState(false);
  const [submittingCommission, setSubmittingCommission] = useState(false);

  // Seta e atualiza os valores dos formulários automaticamente ao carregar o usuário buscado
  useEffect(() => {
    if (selectedUser) {
      setNewNivel(selectedUser?.nivel);
      setNewStatus(selectedUser?.status);
      setNewPlano(selectedUser?.subscriptions?.plan?.product || '');
      setNewClicks(selectedUser?.clicks_per_plan || 0);
      setNewSellerCommission(
        selectedUser?.seller_commission_percentage != null
          ? String(selectedUser.seller_commission_percentage)
          : ''
      );
      setPlanList(selectedUser?.plans || []);
      setUserSubscription(selectedUser?.subscriptions || {});
    }
  }, [selectedUser]);

  // Método de Busca por E-mail
  const handleSearchUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchEmail.trim()) {
      toast.error(translateInterfaceText("Por favor, digite um e-mail."));
      return;
    }
    try {
      setModalLoading(true);
      const response = await authApi.get(`/management/getone/${encodeURIComponent(searchEmail)}`);

      if (response.data.success && response.data.data) {
        setSelectedUser(response.data.data);
        setIsModalOpen(false);
        setSearchEmail('');
        toast.success(translateInterfaceText("Usuário carregado com sucesso!"));
      } else {
        toast.error(translateInterfaceText("Usuário não encontrado."));
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao buscar usuário."));
    } finally {
      setModalLoading(false);
    }
  };

  // Sub-ação 1: Mudança de Nível
  const handleUpdateNivel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      setSubmittingNivel(true);
      await authApi.put(`/management/updateone/${encodeURIComponent(selectedUser?.email)}`, { nivel: newNivel });
      toast.success(translateInterfaceText("Nível do usuário atualizado com sucesso!"));
      setSelectedUser(prev => prev ? { ...prev, nivel: newNivel as any } : null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao atualizar nível."));
    } finally {
      setSubmittingNivel(false);
    }
  };

  // Sub-ação 2: Mudança de Status
  const handleUpdateStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      setSubmittingStatus(true);
      await authApi.put(`/management/updateone/${encodeURIComponent(selectedUser?.email)}`, { status: newStatus });
      toast.success(translateInterfaceText("Status da conta atualizado!"));
      setSelectedUser(prev => prev ? { ...prev, status: newStatus } : null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao atualizar status."));
    } finally {
      setSubmittingStatus(false);
    }
  };

  // Sub-ação 3: Mudança de Plano
  const handleUpdatePlano = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      setSubmittingPlano(true);
      const response = await authApi.put(`/management/updatuserplan/${encodeURIComponent(selectedUser?.email)}`, { id_stripe_plan: newPlano });
      toast.success(translateInterfaceText("Inscrição/Plano modificado com sucesso!"));
      setSelectedUser(response.data.data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao alterar plano."));
    } finally {
      setSubmittingPlano(false);
    }
  };

  // Sub-ação 4: Mudança de Cliques
  const handleUpdateClicks = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      setSubmittingClicks(true);
      await authApi.put(`/management/updateone/${encodeURIComponent(selectedUser?.email)}`, { clicks_per_plan: Number(newClicks) });
      toast.success(translateInterfaceText("Métrica de cliques atualizada com sucesso!"));
      setSelectedUser(prev => prev ? { ...prev, clicks_per_plan: Number(newClicks) } : null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao atualizar cliques."));
    } finally {
      setSubmittingClicks(false);
    }
  };

  // Sub-ação 5: Comissão individual do Seller (vazio = comissão padrão global)
  const handleUpdateSellerCommission = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      setSubmittingCommission(true);
      const trimmed = newSellerCommission.trim();
      const payloadValue = trimmed === '' ? null : Number(trimmed);
      const response = await authApi.put('/stripe/afiliate/finance/admin/seller-commission', {
        targetUserId: selectedUser.id_user,
        seller_commission_percentage: payloadValue,
      });
      const saved = response.data?.data?.seller_commission_percentage ?? payloadValue;
      toast.success(translateInterfaceText("Comissão do seller atualizada com sucesso!"));
      setSelectedUser(prev => prev ? { ...prev, seller_commission_percentage: saved } : null);
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(message || translateInterfaceText("Erro ao atualizar comissão do seller."));
    } finally {
      setSubmittingCommission(false);
    }
  };

  if (!user || user.nivel !== 'admin') return null;

  return (
    <Layout>
      <Header title={translateInterfaceText("Ferramentas Administrativas")} />
      <div className="p-8 max-w-4xl mx-auto space-y-8 font-sans antialiased text-slate-800 pb-28">

        {/* BANNER PRINCIPAL DE AÇÕES */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
          <div>
            <h2 className="text-md font-bold text-slate-900">{translateInterfaceText("Ferramentas de Administrador")}</h2>
            <p className="text-xs text-slate-400 mt-0.5">{translateInterfaceText("Busque um usuário para gerenciar nível, status, plano e cliques operacionais.")}</p>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-95 shrink-0"
          >
            <Search size={15} /> {" "}{translateInterfaceText("Buscar Usuário")}</button>
        </div>

        {/* MODAL DE BUSCA POR E-MAIL */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm top-[-5%]">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-xl overflow-hidden max-w-[600px]">
              <div className="p-5 border-b border-slate-50 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Search size={16} className="text-blue-600" />
                  <h3 className="text-sm font-bold text-slate-900">{translateInterfaceText("Buscar Usuário por E-mail")}</h3>
                </div>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg transition-colors">
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleSearchUser} className="p-5 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{translateInterfaceText("E-mail Corporativo ou Pessoal")}</label>
                  <input
                    type="email"
                    required
                    placeholder={translateInterfaceText("Digite o e-mail completo do usuário")}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 outline-none focus:bg-white focus:border-blue-500 transition-all"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50">
                    {translateInterfaceText("Cancelar")}</button>
                  <button type="submit" disabled={modalLoading} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-[0.99]">
                    {modalLoading ? translateInterfaceText("Buscando...") : translateInterfaceText("Enviar")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* EXIBIÇÃO CONDICIONAL CASO EXISTA UM USUÁRIO RETORNADO */}
        {selectedUser ? (
          <div className="space-y-6">

            {/* COMPONENTE DE RESUMO (RESUME) */}
            <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-50">
                <User size={18} className="text-blue-600" />
                <h3 className="text-sm font-bold text-slate-990 uppercase tracking-wide">{translateInterfaceText("RESUME")}</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-xs">
                <div>
                  <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">{translateInterfaceText("Nome:")}</p>
                  <p className="font-bold text-slate-800 text-sm mt-0.5">{selectedUser.nome} {selectedUser.sobrenome}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">{translateInterfaceText("Email:")}</p>
                  <p className="font-medium text-slate-700 mt-0.5">{selectedUser.email}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">{translateInterfaceText("Telefone:")}</p>
                  <p className="font-medium text-slate-700 mt-0.5">{selectedUser.telefone || "(00) 00000-0000"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">{translateInterfaceText("id:")}</p>
                  <p className="font-mono text-slate-500 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded text-[11px] w-max mt-0.5 select-all">{selectedUser.id_user}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">{translateInterfaceText("cpf:")}</p>
                  <p className="font-medium text-slate-700 mt-0.5">{selectedUser.cpf || "000.000.000-00"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">{translateInterfaceText("Clicks Restantes:")}</p>
                  <p className="font-medium text-slate-700 mt-0.5">{selectedUser.clicks_per_plan || 0}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">{translateInterfaceText("Comissão do Seller:")}</p>
                  <p className="font-medium text-slate-700 mt-0.5">
                    {selectedUser.nivel === 'seller'
                      ? (selectedUser.seller_commission_percentage != null
                          ? translateInterfaceText("{value0}% (individual)", { value0: (selectedUser.seller_commission_percentage) })
                          : translateInterfaceText("Padrão global"))
                      : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">{translateInterfaceText("status da inscrição:")}</p>
                  <p className="font-medium text-slate-700 mt-0.5">{userSubscription.status || ""}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">{translateInterfaceText("customer id:")}</p>
                  <p className="font-medium text-slate-700 mt-0.5">{selectedUser.id_Stripe_Client || ""}</p>
                </div>
              </div>
            </div>

            {/* GRID DE SUB-AÇÕES / FORMULÁRIOS INDEPENDENTES */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* FORM 1: MUDANÇA DE NÍVEL */}
              <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between space-y-4">
                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Shield size={14} className="text-blue-500" />
                    <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Nível de Acesso")}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed">{translateInterfaceText("Form de mudança de nível do usuário. Ela é setada com o nível atual do usuário automaticamente.")}</p>
                </div>
                <form onSubmit={handleUpdateNivel} className="space-y-3">
                  <select
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-600 outline-none cursor-pointer hover:bg-slate-100/50 focus:bg-white transition-all"
                    value={newNivel}
                    onChange={(e) => setNewNivel(e.target.value)}
                  >
                    <option value="customer">customer</option>
                    <option value="seller">seller</option>
                    <option value="manager">manager</option>
                    <option value="admin">admin</option>
                    <option value="support">support</option>
                    <option value="platform">platform</option>
                  </select>
                  <button type="submit" disabled={submittingNivel} className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-[0.99]">
                    {submittingNivel ? translateInterfaceText("Salvando...") : translateInterfaceText("Enviar")} <Send size={12} />
                  </button>
                </form>
              </div>

              {/* FORM 2: MUDANÇA DE STATUS */}
              <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between space-y-4">
                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Power size={14} className="text-emerald-500" />
                    <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Status do Cadastro")}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed">{translateInterfaceText("Form de mudança de status do usuário, aceitando os parâmetros operacionais de Ativo (A) ou Desativado (D).")}</p>
                </div>
                <form onSubmit={handleUpdateStatus} className="space-y-3">
                  <select
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-600 outline-none cursor-pointer hover:bg-slate-100/50 focus:bg-white transition-all"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                  >
                    <option value="A">{translateInterfaceText("A = Ativo")}</option>
                    <option value="D">{translateInterfaceText("D = Desativado")}</option>
                  </select>
                  <button type="submit" disabled={submittingStatus} className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-[0.99]">
                    {submittingStatus ? translateInterfaceText("Salvando...") : translateInterfaceText("Enviar")} <Send size={12} />
                  </button>
                </form>
              </div>

              {/* FORM 3: MUDANÇA DO PLANO */}
              <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between space-y-4">
                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <CreditCard size={14} className="text-indigo-500" />
                    <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Plano / Subscription")}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed">{translateInterfaceText("Form de mudança do plano/subscription do usuário, exibindo dinamicamente o nome do plano atual.")}</p>
                </div>
                <form onSubmit={handleUpdatePlano} className="space-y-3">
                  <select
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-600 outline-none cursor-pointer hover:bg-slate-100/50 focus:bg-white transition-all"
                    value={newPlano}
                    onChange={(e) => setNewPlano(e.target.value)}
                  >
                    <option value="">{translateInterfaceText("Nenhum / Sem Plano Ativo")}</option>
                    {plansList && plansList.map(plan => (
                      <option key={plan.product} value={plan.product}>{plan.nickname}</option>
                    ))}
                  </select>
                  <button type="submit" disabled={submittingPlano} className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-[0.99]">
                    {submittingPlano ? translateInterfaceText("Salvando...") : translateInterfaceText("Enviar")} <Send size={12} />
                  </button>
                </form>
              </div>

              {/* FORM 4: QUANTIDADE DE CLICKS */}
              <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between space-y-4">
                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <MousePointerClick size={14} className="text-amber-500" />
                    <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Volume Contratado")}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed">{translateInterfaceText("Form de mudança da quantidade de clicks do usuário. É um valor numérico manipulável diretamente.")}</p>
                </div>
                <form onSubmit={handleUpdateClicks} className="space-y-3">
                  <input
                    type="number"
                    min="0"
                    required
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 outline-none focus:bg-white transition-all"
                    value={newClicks}
                    onChange={(e) => setNewClicks(Number(e.target.value))}
                  />
                  <button type="submit" disabled={submittingClicks} className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-[0.99]">
                    {submittingClicks ? translateInterfaceText("Salvando...") : translateInterfaceText("Enviar")} <Send size={12} />
                  </button>
                </form>
              </div>

              {/* FORM 5: COMISSÃO INDIVIDUAL DO SELLER (somente para nível seller) */}
              {selectedUser.nivel === 'seller' && (
                <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col justify-between space-y-4">
                  <div>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Percent size={14} className="text-rose-500" />
                      <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Comissão Individual")}</span>
                    </div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">{translateInterfaceText("Defina um percentual exclusivo para este seller. Deixe vazio para usar a comissão padrão global.")}</p>
                  </div>
                  <form onSubmit={handleUpdateSellerCommission} className="space-y-3">
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder={translateInterfaceText("Padrão global")}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 pr-8 text-xs font-semibold text-slate-700 outline-none focus:bg-white transition-all"
                        value={newSellerCommission}
                        onChange={(e) => setNewSellerCommission(e.target.value)}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">%</span>
                    </div>
                    <button type="submit" disabled={submittingCommission} className="w-full flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-[0.99]">
                      {submittingCommission ? translateInterfaceText("Salvando...") : translateInterfaceText("Enviar")} <Send size={12} />
                    </button>
                  </form>
                </div>
              )}

            </div>
          </div>
        ) : (
          /* PÁGINA EM ESTADO INICIAL */
          <div className="bg-white border border-slate-100 rounded-2xl p-12 text-center shadow-sm max-w-xl mx-auto space-y-3">
            <Info size={20} className="text-slate-400 mx-auto" />
            <h3 className="text-sm font-bold text-slate-800">{translateInterfaceText("Nenhum usuário selecionado")}</h3>
            <p className="text-xs text-slate-400 max-w-xs mx-auto">{translateInterfaceText("Clique no botão \"Buscar Usuário\" localizado no topo para pesquisar e gerenciar uma conta por e-mail.")}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}